import { useState } from 'react';
import PropTypes from 'prop-types';

import { Link } from 'react-router-dom';

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import Image from 'next/image';

import Button from 'Components/LegacyButton';
import Spacer from 'Components/Spacer';
import Typography from 'Components/Typography';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import { customSkincare as customSkincareData } from 'assets/content/home';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const badgeWidth = 123;
const badgeHalfDimension = badgeWidth / -2;
const rotation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const HeroSectionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  background: ${props => props.theme.palette.common.beige.darker};
  margin: 0 auto;

  ${props => props.theme.breakpoints.up('md')} {
    grid-template-columns: 20% auto;
  }
  ${props => props.theme.breakpoints.up('lg')} {
    max-width: 1200px;
  }
`;

const HeroInfoContainer = styled.div`
  padding: ${props => props.theme.spacing.s56}px ${props => props.theme.spacing.s32}px;
  ${props => props.theme.breakpoints.up('md')} {
    padding-top: ${props => props.theme.spacing.s144}px;
    grid-column: 1 / -1;
    grid-row: 1 / -1;
  }
`;

const Badge = styled.div`
  position: absolute;
  overflow: hidden;
  bottom: ${badgeHalfDimension}px;
  right: ${({ theme }) => theme.spacing.s24}px;
  z-index: 100;

  ${props => props.theme.breakpoints.up('md')} {
    left: ${badgeHalfDimension}px;
    top: ${({ theme }) => theme.spacing.s144}px;
  }
`;

const BadgeRotatingContent = styled(Image)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 3px;
  animation: ${rotation} 9s linear infinite;
`;

const HeroImageContainer = styled.div`
  position: relative;
  width: 100%;

  ${props => props.theme.breakpoints.up('md')} {
    position: absolute;
    right: 0;
    top: -35px;
    width: 58%;
  }
  ${props => props.theme.breakpoints.up('lg')} {
    width: 60%;
    top: -65px;
    max-width: 990px;
  }
`;

const HeroImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
  border-top: 1px solid;
  border-bottom: 1px solid;
  ${props => props.theme.breakpoints.up('md')} {
    border: 1px solid;
    border-right: none;
  }
`;

const HeroImageContainerGhost = styled.div`
  grid-row: 1 / -1;
`;

const HeroImageGhost = styled.img`
  opacity: 0;
  object-fit: cover;
  width: 100%;
  height: 100%;
`;
const StyledHeroTitle = styled(Typography)`
  max-width: 350px;
`;
const StyledHeroPrimaryTitle = styled(Typography)``;
const StyledHeroDescription = styled(Typography)`
  max-width: 265px;
  margin-bottom: 16px;
`;

const HeroSection = ({ content }) => {
  const {
    title,
    primaryTitle,
    cta,
    description,
    imageMobile,
    imageDesktop,
    imageAlt,
    badgeBackground,
    badgeRotatingContent,
  } = content;
  const { isMobile, isDesktop } = useResponsiveVariant();

  // For accessibility purpose: infinite animation should be stoppable
  const [animationRunning, setAnimationRunning] = useState(true);

  return (
    <HeroSectionContainer>
      <HeroImageContainer>
        <Badge>
          <Image
            alt={badgeBackground.alt}
            height={badgeWidth}
            onClick={() => {
              setAnimationRunning(!animationRunning);
            }}
            onKeyPress={() => {
              setAnimationRunning(!animationRunning);
            }}
            src={badgeBackground.src}
            tabIndex="0"
            width={badgeWidth}
          />
          <BadgeRotatingContent
            alt={badgeRotatingContent.alt}
            height={badgeWidth}
            src={badgeRotatingContent.src}
            style={{
              animation: !animationRunning && 'none',
            }}
            width={badgeWidth}
          />
        </Badge>
        <HeroImage alt={imageAlt} src={isMobile ? imageMobile.src : imageDesktop.src} />
      </HeroImageContainer>
      {isDesktop && (
        <HeroImageContainerGhost>
          <HeroImageGhost alt={`${imageAlt}-invsible`} src={imageDesktop.src} />
        </HeroImageContainerGhost>
      )}
      <HeroInfoContainer>
        <StyledHeroTitle variant="mono1">{title}</StyledHeroTitle>
        <Spacer size={12} />
        <StyledHeroPrimaryTitle variant={isMobile ? 'h1' : 'hero2'}>
          {primaryTitle}
        </StyledHeroPrimaryTitle>
        <Spacer size={16} />
        <StyledHeroDescription>{description}</StyledHeroDescription>
        <Button
          Component={Link}
          data-click="explore-skincare"
          data-from="skincare-grid"
          to={cta.path}
          variant="noir"
        >
          {cta.label}
        </Button>
      </HeroInfoContainer>
    </HeroSectionContainer>
  );
};

HeroSection.defaultProps = {
  content: customSkincareData,
};

HeroSection.propTypes = {
  content: PropTypes.shape({
    imageAlt: PropTypes.string,
    imageMobile: srcOrNextImageProp,
    imageDesktop: srcOrNextImageProp,
    title: PropTypes.string,
    primaryTitle: PropTypes.string,
    description: PropTypes.string,
    cta: PropTypes.shape({
      label: PropTypes.string,
      path: PropTypes.string,
    }),
    badgeBackground: PropTypes.shape({
      src: srcOrNextImageProp,
      alt: PropTypes.string,
      dataTest: PropTypes.string,
    }),
    badgeRotatingContent: PropTypes.shape({
      src: srcOrNextImageProp,
      alt: PropTypes.string,
    }),
  }),
};

export default HeroSection;
